import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import OpeningRun from '../../components/OpeningRun'
import { api } from '../../lib/api'
import { useT } from '../../lib/i18n'
import type { Door } from '../../lib/contract'
import {
  disableLineNotifications,
  enableLineNotifications,
  lineNotificationsEnabled,
  lineNotificationsSupported,
} from '../../lib/lineNotifications'
import { useMessagingSession } from './useMessagingSession'
import Thread from './parts/Thread'
import Composer from './parts/Composer'
import Corridor from './parts/Corridor'
import styles from './Surface.module.css'
import theme from './theme.module.css'

/**
 * THE MESSAGING SURFACE — the third house.
 *
 * One being, one thread. The presence form (`PresencePrompt`) rendered as what it
 * literally is, per the mode ruling (Sanctum revival note §41, 2026-08-19): the
 * player is not in a room with anyone; they write, and someone somewhere writes
 * back. Its cheapness is the point — the reader is doing the thing the fiction
 * depicts, so nothing has to be translated and no apparatus is owed
 * (`docs/specs/2026-08-19-the-two-axes.md`).
 *
 * THE STARVED SURFACE. This borrows the clothes of the most engagement-optimised
 * form factor ever built, so what it refuses is stated where it is implemented,
 * not just in doctrine: no unread counts, no badges, no last-seen, no read
 * receipts, no notifications, nothing that accumulates
 * (`2026-08-19-the-many-channels-format.md` §2.3–§4). The thread is a place you
 * go, never a thing that arrives.
 *
 * ⚠ TYPING INDICATORS ARE NO LONGER ON THAT LIST (founder-ruled 2026-08-31). They are not
 * an accumulation mechanic: you must already be present to see one, so it cannot summon
 * anybody, it reports on this exchange rather than on the other party's life outside it,
 * and it expires leaving nothing behind. The founder's reason: *information about the
 * conversation — the person is currently trying to communicate — needed for respectful
 * communication.* ⚠ GUARD: it must never be a lie. Only while lines that genuinely exist
 * are queued to arrive; never speculative, never on an idle thread, never a claim that
 * someone is about to write. See `docs/specs/2026-08-19-the-many-channels-format.md` §2.3.
 *
 * ── TWO LEVELS, NO TABS (2026-08-22 — the hall; UI spec §5, addendum 10 item 6) ────────────
 * A pack with personas has a CORRIDOR: the opening run, then the threshold door, then the
 * guest's own doors — home, the hall, her slot. Tapping one opens that person's thread; back
 * returns to the corridor. A pack with one person never sees any of it and goes straight in,
 * which is every pack before the hall.
 *
 * ── ONE DOOR IS NOT A CORRIDOR (2026-08-25 — pack-per-conversation) ─────────────────────────
 * A pack whose `/threads` answer holds exactly ONE door is entered at that door, hall or no hall,
 * and back goes to the shelf rather than to a room with one row in it. That is what a
 * CONVERSATION pack is: one persona, one thread, its own address (`packs/the-argument` today, and
 * every Chats row after it). Everything downstream of the entry — session, resume, burst,
 * doorstep — is the corridor's own path unchanged, because a row on the shelf and a row in a
 * corridor are the same act.
 *
 * ── AND THE DEEP LINK, WHICH OUTLIVED THE PACK IT WAS BUILT FOR ─────────────────────────────
 * `door` names one persona of several and skips the corridor the same way. It was how the four
 * conversations were addressed while they shared one pack; nothing generates such a link now, and
 * it is kept because it is generic, harmless, and the honest way to address one room of a hall.
 * See the `door` prop.
 *
 * ── THEMING ───────────────────────────────────────────────────────────────────────────────
 * The look is PACK-SCOPED, never global. A pack opts in by naming a theme in the client's
 * scenario catalog; a pack that names none renders exactly what it rendered before — not by
 * care but by construction, since without the class none of the themed rules can match.
 */
export default function Messaging({
  packId,
  agreedPriceMicros,
  theme: themed = false,
  startFresh = false,
  door,
}: {
  packId?: string
  /**
   * ⚠ THE PRICE THE PLAYER AGREED TO, in micros (2026-08-27 — the wallet). Carried from the
   * threshold card straight to `startSession`, where the server checks it against today's plan
   * and refuses a mismatch rather than charging the new price. Never recomputed on the way down:
   * this is a promise the player made about a specific number.
   *
   * Undefined = no price applied (an unpriced pack, or a build with no wallet).
   */
  agreedPriceMicros?: number
  /** Render in the Hildur look — paper on wood, the door screen, the ruled composer. */
  theme?: boolean
  /** The reset control: abandon the existing thread rather than resuming it. */
  startFresh?: boolean
  /**
   * ⚠ ENTER AT ONE PERSON'S THREAD, WITH NO CORRIDOR (2026-08-25 — the CONVERSATIONS shelf).
   *
   * A persona id, from `/p/:packId?door=…` (see PackSurface). The corridor is skipped, the pack's
   * opening run is skipped, and the thread is entered exactly as if the row had been tapped in
   * the corridor — same session, same resume, same burst — with the shelf as the way back.
   *
   * ⚠ NOTHING PASSES IT ANY MORE. It was built for the four conversations while they shared
   * `characters-poc`; under pack-per-conversation each of them is a one-door pack and enters
   * itself (see the corridor read below). Kept because it is generic and is the honest way to
   * address ONE ROOM OF A HALL, which is a thing no shelf row can do today and a streamer's link
   * may want tomorrow. Undefined everywhere, so every branch that tests it is dead — which is
   * what leaves Hildur's flow untouched by construction rather than by care.
   */
  door?: string
}) {
  const t = useT()
  const navigate = useNavigate()
  const [search] = useSearchParams()

  /*
   * THE CORRIDOR'S OWN READ, done once here rather than inside the session hook — the hook
   * opens ONE thread and this screen is about which threads exist. Null while unknown, so the
   * surface neither flashes a corridor at a one-person pack nor a thread at a hall.
   */
  const [doors, setDoors] = useState<Door[] | null>(null)
  const [hasCorridor, setHasCorridor] = useState(false)
  const [at, setAt] = useState<Door | null>(null)

  /*
   * THE DOORSTEP (founder-ruled 2026-08-23) — resume, or start new, asked once at the door and
   * only when there is something to resume. It replaced the standing *start over* control on this
   * grammar; see `parts/ResumeChoice.tsx` for why a question beats a button here.
   *
   * ⚠ STARTING NEW RESETS THE WHOLE PACK, NOT ONE THREAD. A half-reset hall is incoherent: a fresh
   * home thread beside an already-opened Una would skip the reception and withhold her roster
   * line, so the guest would begin again in a house that half-remembers them. `fresh` therefore
   * holds for EVERY thread opened this visit, and every door is treated as unopened.
   *
   * ⚠ IT DOES NOT DELETE ANYTHING. New threads supersede the old ones — `/threads` takes the
   * newest per persona — so nothing is destroyed by a tap, which is the right asymmetry for a
   * control that ends a relationship.
   *
   * ⚠ THE SCOPE DEBT IS PAID, AND NOT ONE LINE OF THIS CODE MOVED (2026-08-25 —
   * pack-per-conversation). Worth keeping, because it is a case of the right fix being upstream.
   *
   * What was recorded here for a day: the doorstep is PACK-scoped, which was coherent while a
   * pack was one hall — you are asked at the front door of a house, about the house — and wrong
   * the moment four conversations shared `characters-poc`, where the question got asked at
   * Dracula's door because NAPOLEON had a thread, and *start again* there abandoned all four.
   *
   * The split makes a pack one conversation again, so PACK-SCOPE IS ROW-SCOPE by construction and
   * the question can only ever be about the conversation it is asked in front of. The alternative
   * — scoping the reset per persona — would have meant building exactly the half-reset hall the
   * paragraph above rules out. A hall still resets as a hall, and that is still right.
   */
  /*
   * ⚠ THE DOORSTEP IS RETIRED (founder, 2026-09-14): the thread always continues, and starting
   * over is the standing corner control above every grammar again (`PackSurface`, `played()`),
   * armed twice and naming its cost. `choice` therefore begins at *resume* and never asks; the
   * machinery below it is kept as it was, because *start again* still arrives through
   * `startFresh` and everything downstream of `fresh` is still right.
   */
  const [choice] = useState<'ask' | 'resume' | 'fresh'>('resume')
  const fresh = choice === 'fresh' || startFresh

  useEffect(() => {
    if (!packId) return
    let live = true
    void api
      .getThreads(packId)
      .then((r) => {
        if (!live) return
        setDoors(r.doors)
        setHasCorridor(r.corridor)
        /*
         * ⚠ ONE DOOR IS NOT A CORRIDOR (2026-08-25 — pack-per-conversation).
         *
         * Two shapes arrive here and both go straight in. A pack with NO personas answers
         * `corridor: false` and one nameless door — every pack that predates the hall. A pack
         * with exactly ONE persona answers `corridor: true` and one named door — every
         * CONVERSATION pack, a conversation being one person by definition. Drawing a hall with a
         * single row in it would be a screen whose only purpose is to be tapped through.
         *
         * ⚠ THE TEST IS THE ANSWER, NOT THE PACK — `doors` is what THIS guest may see, and dark
         * rooms are absent from it. So a hall that has so far introduced only one of its people
         * also enters directly, which is right for the same reason: there is nothing else on the
         * page to choose. Hildur can never be in that state (Una, Salvör and Ásta all stand
         * introduced from the start), so her flow is untouched by construction, not by care.
         */
        if (!r.corridor || r.doors.length === 1) setAt(r.doors[0] ?? null)
        /*
         * THE DEEP LINK, RESOLVED IN THE SAME BREATH AS THE DOORS — so `at` is set on the very
         * render that first knows the pack has a corridor, and the corridor is never on screen
         * for a frame. Set here rather than in an effect for exactly that reason.
         *
         * An id that matches nothing leaves `at` null and lands on the corridor, which is
         * `PackSurface`'s own rule for an unknown door one level up: fall back, never guess.
         */
        else if (door) setAt(r.doors.find((d) => d.personaId === door) ?? null)
      })
      // A corridor we cannot read is not a reason to withhold the pack: fall back to the
      // pack's own single thread, which is what every pack did before the hall existed.
      .catch(() => live && setDoors([]))
    return () => {
      live = false
    }
  }, [packId, door])

  /*
   * ⚠ THE HOME THREAD MUST HAVE ONE IDENTITY (bug, 2026-08-23).
   *
   * The corridor is rendered with no persona chosen, and the thread open underneath it is the
   * home one — so this used to pass `undefined` there and `'bergur'` the moment his row was
   * tapped. Same thread, two keys: the session effect re-ran, re-resolved and re-fetched the
   * very conversation already open, which cost a `/threads` + revive + opening + history round
   * trip, briefly blanked the thread, and — because the opening gate is honestly false until
   * the guest writes a line — HANDED BACK THE OPENING RUN. That last one is what was reported.
   *
   * Naming home explicitly makes the key identical on both sides of the tap, so nothing
   * re-opens and there is nothing to re-show. The `||` chain (not `??`) is load-bearing: a
   * pack with no personas sends an empty personaId, and the server refuses `""`.
   */
  const home = doors?.find((d) => d.place === 'home') ?? null

  /*
   * ⚠ FRESH MEANS ONCE PER THREAD, NOT EVERY VISIT TO IT (bug, 2026-08-23). `fresh` holds for the
   * whole visit so that every door opens clean after *start again* — but left unqualified it also
   * meant walking Una → home → Una → home abandoned and re-created the home thread each time,
   * throwing away the conversation the guest had just had in it. A reset resets what exists; it
   * does not keep resetting.
   */
  /*
   * ⚠ NOTHING OPENS UNTIL THE GUEST HAS ANSWERED (bug, 2026-08-23: "when you resume Hildur, the
   * opening cards fire again").
   *
   * The first fix passed the doorstep's answer into the session hook — and was too late, because
   * hooks cannot be conditional: the hook had already begun loading home while the doorstep was
   * still on screen, and had already fetched the opening, so *continue* arrived after the cards
   * were in hand.
   *
   * Gating the hook on the answer fixes the ordering AND removes a waste the first version hid:
   * tapping *start again* was abandoning a session created two seconds earlier, on the very screen
   * whose purpose is deciding whether to have one.
   */
  const doorstepDue =
    doors !== null && choice === 'ask' && !startFresh && doors.some((d) => d.sessionId)

  /*
   * STANDING IN THE DEEP-LINKED THREAD — the narrow, provable claim.
   *
   * Not `Boolean(door)`: an id that matched nothing dropped the reader on the corridor, and from
   * there a tapped row is an ordinary corridor entry that must behave like one. So the claim is
   * *the door the URL named is the door we are standing in*, and it is false for every pack that
   * passes no door at all — which, since the split, is every pack.
   *
   * It gates ONE thing: whether the pack's opening run is withheld (see `alreadyWalked`). A
   * reader who asked for one room of a hall by name did not ask for the hall's front door.
   */
  const direct = at !== null && at.personaId === door

  /*
   * THE WHOLE PACK IS THIS ONE THREAD (2026-08-25 — pack-per-conversation).
   *
   * Deliberately separate from `direct` above, because the two claims differ where it matters. A
   * one-door pack IS its front door, so its opening run is its own and must still be walked —
   * exactly as every pre-hall pack walked it. What it has no notion of is a corridor, so the way
   * BACK cannot be one.
   */
  const sole = doors !== null && doors.length === 1

  /*
   * ⚠ AND THE ONE WAY BACK IS NOT ALWAYS THE SAME PLACE. Walked in from a corridor, back is the
   * corridor. Entered as the whole pack, or opened at a named door from outside, back is the
   * SHELF — dropping that reader into a hall they never walked would invent a middle they did not
   * choose, and for a one-door pack the hall does not exist at all.
   */
  const backToShelf = direct || sole

  const openedThisVisit = useRef(new Set<string>())
  const personaKey = at?.personaId || home?.personaId || ''

  /*
   * LINE NOTIFICATIONS, LAYER 0 (2026-09-14). One control, a bell, on only when this reader
   * pressed it in this browser and the browser agreed. What it notifies is his lines, as his
   * lines, while the tab is not being looked at — nothing else, ever (`lib/lineNotifications.ts`).
   */
  const [notify, setNotify] = useState(lineNotificationsEnabled)
  const toggleNotify = async () => {
    if (notify) {
      disableLineNotifications()
      setNotify(false)
    } else {
      setNotify(await enableLineNotifications({ title: 'Heimar', body: t('thread.notifyOff') }))
    }
  }

  /*
   * THE JOIN NOTICE (2026-08-26, founder-ruled) — the SITE's line over the blank glass while the
   * thread is being opened.
   *
   * The first thing on screen was nothing: the opening call takes a few seconds and the reader
   * sat in front of empty paper. This fills that with fiction rather than with apparatus — the
   * reader booked a slot on a marketplace, and a marketplace shows a session-join state.
   *
   * ⚠ THE PLATFORM'S VOICE, NEVER HIS AND NEVER THE APP'S. It is not a message: it is not
   * attributed, does not enter the record, and is never styled as a bubble (see `.joining`). His
   * own first words arrive on the thread as his burst, from the server, like any other line.
   *
   * ⚠ NEVER A PRESENCE CLAIM — no "online", no "waiting for you". That is the summons
   * never-summons forbids (`presence-format.md` §2.1); the pack's own slot carries the same
   * warning where the words are authored.
   *
   * ⚠ REAL STATE, NOT A TIMER. Read off `s.status` and `s.connecting`, so it appears while the
   * session is genuinely starting and — on a FRESH open only — through the pick-up beat, until
   * his first authored line is shown (the hook's staged arrival; his lines are being written now,
   * in the fiction, and take their typing time). Gone the frame the first line lands, or the
   * frame it fails. A revived thread never shows it past the start call: history paints whole,
   * because replaying the past slowly would be the surface performing, which it must never do.
   *
   * Whose door: the one being stood in, falling back to home, because that is precisely the
   * persona the session hook is opening (see its `personaId` below). Null renders nothing, which
   * is every pack that authors no join — a chat with no marketplace between the two people.
   */
  const joinNotice = (at ?? home)?.joinNotice ?? null
  // The pick-up, pack-authored (null = the hook's default). Same door the notice reads.
  const connectMs = (at ?? home)?.connectMs ?? null

  /*
   * ⚠ THE DOORSTEP IS NOT A VISIT TO THE THREAD (bug, 2026-08-28: "you can't reset a session").
   *
   * This used to mark the persona the moment it became CURRENT, with no `doorstepDue` term — and
   * the doorstep is exactly when a persona becomes current without anything being opened. So:
   * `/threads` resolves, this marks `profesora`, the *continuar / empezar de nuevo* screen appears,
   * the guest taps **start again** — and `startFresh` below reads `fresh && !has(personaKey)`,
   * which is now `true && !true`. The hook took the resume path and revived the very thread the
   * guest had just asked to be rid of.
   *
   * It made *start again* impossible on every pack that had a thread to start again FROM, which is
   * every pack the control is ever shown on. Marta and Indriði both, and no new session row was
   * ever written for either.
   *
   * ⚠ THE 08-23 GUARD THIS NARROWS IS STILL RIGHT and must not be removed: without it, walking
   * Una → home → Una → home abandoned and re-created the home thread on every pass, throwing away
   * the conversation just had in it. A reset resets what exists; it does not keep resetting. The
   * defect was only ever the MOMENT of marking — the ref is named for sessions opened, so it must
   * be written where one is opened, and the doorstep opens nothing.
   *
   * Ordering is what makes this correct rather than lucky: effects run after render, so on the
   * render where `doorstepDue` first goes false the ref is still empty, `startFresh` is true, and
   * the fresh session opens. This marks it immediately after, and the hook's own `startGuard`
   * holds the thread to one open per mount from then on.
   */
  useEffect(() => {
    if (personaKey && !doorstepDue) openedThisVisit.current.add(personaKey)
  }, [personaKey, doorstepDue])

  const s = useMessagingSession(
    /*
     * NOT UNTIL THE DOORS ARE KNOWN — otherwise the first render opens home under one key and
     * the corridor read immediately re-opens it under another, which is the same bug with an
     * extra session paid for.
     *
     * ⚠ `''`, never `undefined`: the hook falls back to the ROUTE's pack id when this is
     * nullish, and for a door-compiled pack that is a different pack. `''` is its own
     * "no pack yet" sentinel and the effect returns on it.
     */
    doors === null || doorstepDue ? '' : packId,
    {
      personaId: at?.personaId || home?.personaId || undefined,
      connectMs,
      startFresh: fresh && !openedThisVisit.current.has(personaKey),
      agreedPriceMicros,
      notifyAs: (at ?? home)?.name,
      /*
       * ⚠ A DEEP LINK DOES NOT WALK THE PACK'S RUN, so it must not be handed one.
       *
       * The opening run is the PACK's threshold — the thing you cross on the way into a hall —
       * and it is authored onto the home persona. A reader who named one room of a hall asked for
       * that room, not for the house's front door; handing them the pack's cards first would be
       * the corridor by another name.
       *
       * ⚠ `direct` AND NOT `sole`, and the distinction is the whole reason the two are separate
       * consts. A one-door pack has no front door but its own, so its run — if it authors one —
       * is this reader's run and is shown, which is what every pack did before halls existed. The
       * four conversation packs author none, so nothing changed for them either way.
       *
       * `alreadyWalked` is the hook's existing "do not show me the run" lever (the doorstep's
       * *continue* uses it), and its guarantee — nothing is shown, nothing is skipped
       * server-side — is exactly the one wanted here. The run is not consumed or marked; it is
       * simply not this row's screen. It still meets anyone who enters the pack by its own front.
       */
      alreadyWalked: choice === 'resume' || direct,
    },
  )

  /*
   * THE PLACED REVEAL — flagged, and default OFF (spec §2.2: "ship it flagged, measure reading
   * comfort on the bench"). `?reveal=on|off` overrides so the two can be compared inside ONE
   * session without a rebuild — what a reading-comfort judgement actually needs, since the
   * thing being judged is how it feels across a run of his lines.
   *
   * ⚠ NOTHING IS MEASURED IN CODE. No timing, no tap-rate, no telemetry of any kind — the
   * covenant does not get suspended because the number would be interesting. The bench is a
   * person reading, and the tell is whether they tap to complete.
   */
  const placedReveal = search.get('reveal') === 'on'

  /*
   * ⚠ UNA IS THE FIRST PERSON THE GUEST MEETS (founder-ruled 2026-08-23; POC note §1.1).
   *
   * The four cards, then her — three lines: who she is, what to ask her for, *go on down* — and
   * then the family. So the run does not hand the guest to the corridor on a first visit; it
   * hands them to the reception, and the corridor is what they come back to.
   *
   * Once only, and the engine decides "once" rather than the client: her thread has messages the
   * moment her burst is seeded, so `opened` is true on every later visit and the landing falls
   * back to the corridor. `sentDown` covers the same MOUNT — walking back to the corridor must
   * not walk the guest straight into her again.
   */
  const [sentDown, setSentDown] = useState(false)

  useEffect(() => {
    /*
     * ⚠ `s.status === 'live'` IS LOAD-BEARING (bug, 2026-08-23). Without it this fired in the gap
     * BEFORE `/opening` had answered — `s.opening` is null then too — so the guest was sent to
     * Una before the run had been fetched, the home load was discarded as superseded, and the
     * four cards reappeared later when they walked back. "No opening" and "no opening YET" are
     * different states and only the status tells them apart.
     */
    if (s.status !== 'live' || s.opening || at !== null || sentDown || doors === null) return
    // ⚠ AND NEVER OVER A DEEP LINK. A reader who asked for one door by name is not handed a
    // housekeeper instead — the reception is the pack's own way in, and this reader did not use
    // it. Unreachable in practice (nothing passes `door` since the split, and a one-door pack has
    // no housekeeper to find), and stated so that a pack which has both a reception and a named
    // door cannot quietly do the wrong thing.
    if (door) return
    const reception = doors.find((d) => d.place === 'housekeeper' && (fresh || !d.opened))
    if (!reception) return
    setSentDown(true)
    setAt(reception)
  }, [s.status, s.opening, at, sentDown, doors, fresh, door])

  if (doors === null) return null

  // The doorstep no longer renders (2026-09-14); `doorstepDue` is false by construction and
  // kept only so the gating it feeds reads as it did.
  if (doorstepDue) return null

  // The opening run owns the screen while it lasts — it is the folklore, and the
  // thread behind it would be answered before it was read.
  if (s.opening) {
    /*
     * ⚠ THE DRAWN DOOR IS GONE (stage 1c, 2026-08-22). It was a separate screen with a wooden
     * door on it; the founder ruled drawn objects out until an artist draws them.
     *
     * What replaced it DELETES rather than restyles: the extra screen, its `knocked` state and
     * the run's whole `hideAction` branch are gone, and the threshold is the run's own last card
     * with STOP A MINUTE as type. The pack's words are the entire control, which is what the
     * threshold was always supposed to be.
     */
    return (
      <OpeningRun
        opening={s.opening}
        onComplete={s.finishOpening}
        actionVariant={themed ? 'type' : 'plate'}
      />
    )
  }

  // The landing screen after the opening: the guest's doors.
  if (hasCorridor && at === null) {
    return (
      <Corridor doors={doors} onEnter={setAt} paper={themed} />
    )
  }

  return (
    <div className={themed ? `${styles.surface} ${theme.hildur}` : styles.surface}>
      {/*
        A ROOM IS THE CORRESPONDENCE WITH A NAME ON IT (spec §5.2) — the same sheet of paper,
        titled, with the way back. No apparatus is added for being in the hall: the room is the
        paper, and nothing inside is drawn.
      */}
      {hasCorridor && at && (
        <div className={styles.titleStrip}>
          {/* ⚠ THE CHEVRON ALONE (founder-ruled 2026-08-23). It said "‹ the corridor", which named
              the guest's map on a page that is a conversation — and the way back needs no name
              when there is only one way back. The word survives as the ARIA label: a bare glyph
              is a label to nobody reading with their ears.

              ⚠ AND THE ONE WAY BACK IS NOT ALWAYS THE SAME PLACE (2026-08-25) — see
              `backToShelf` above for which it is. The label follows the destination; the front
              page is deliberately unnamed, so its label says the direction rather than a name the
              product refuses to give it. */}
          <button
            className={styles.back}
            onClick={() => (backToShelf ? navigate('/') : setAt(null))}
            aria-label={backToShelf ? t('shelf.back') : t('corridor.back')}
          >
            ‹
          </button>
          <span className={styles.who}>{at.name}</span>
        </div>
      )}

      {s.status === 'error' && (
        <p className={styles.trouble} role="alert">
          {s.error ?? t('thread.trouble')}
        </p>
      )}

      {/* Above the thread rather than inside it: the thread is the record, and nothing that is
          not a line anyone wrote belongs in it. `role="status"` so a reader listening is told
          the same thing a reader looking is, and no more.

          THE DEFAULT CONNECTING LINE (2026-08-26, founder): a pack's authored joinNotice speaks
          in the platform's voice; where none is authored, the surface says the one thing the
          PLAYER'S OWN PHONE may honestly say — the channel is being established. That is a
          status of your end, not a claim about him (never "online", never "waiting for you"),
          which is why it is lawful even for a pack whose fiction has no platform at all. */}
      {(s.status === 'starting' || s.connecting) && (
        <p className={styles.joining} role="status">
          {joinNotice ?? t('thread.connecting', { name: (at ?? home)?.name ?? '' })}
        </p>
      )}

      {/* The bell: the only control on the page that is about the reader's own phone rather
          than the thread. Above the record, because it is not a line anyone wrote. */}
      {lineNotificationsSupported() && (
        <div className={styles.tools}>
          <button
            type="button"
            className={notify ? styles.bellOn : styles.bell}
            onClick={() => void toggleNotify()}
            aria-pressed={notify}
            aria-label={
              notify
                ? t('thread.notifyOff')
                : t('thread.notifyOn', { name: (at ?? home)?.name ?? '' })
            }
            title={
              notify
                ? t('thread.notifyOff')
                : t('thread.notifyOn', { name: (at ?? home)?.name ?? '' })
            }
          >
            {notify ? '🔔' : '🔕'}
          </button>
        </div>
      )}

      <Thread
        messages={s.messages}
        awaiting={s.awaiting}
        paper={themed}
        placedReveal={placedReveal}
      />

      {/* Disabled only until the session is live. Never while a reply is coming:
          input is never gated, and adding a line before he answers is ordinary. */}
      <Composer
        onSend={(text) => void s.send(text)}
        onTyping={s.signalTyping}
        disabled={s.status !== 'live'}
        paper={themed}
      />

    </div>
  )
}
